import { ReactNode } from 'react'
import {
  FieldValues,
  useController,
  FieldPath,
  UseControllerProps,
} from 'react-hook-form'
import {
  Autocomplete,
  AutocompleteProps,
  TextField,
  TextFieldProps,
} from '@mui/material'

type AutocompletePropsType<OptionType> = AutocompleteProps<
  OptionType,
  true | false, // multiple
  true, // disable clearable
  false, // free solo - unused by us
  'div' // chip component - unused by us
>

type BaseOptionType = {
  id: string
}

type FormAutocompleteProps<FormValues extends FieldValues, OptionType> = Omit<
  AutocompletePropsType<OptionType>,
  'renderInput' | 'onChange'
> & {
  name: FieldPath<FormValues>
  label: string
  autoFocus?: boolean
  helperText?: ReactNode
  maxSelected?: number
  required?: boolean
  textFieldProps?: Omit<TextFieldProps, 'required'>
  onChange?: (newValue: OptionType | OptionType[] | null) => void
  useControllerProps?: Omit<UseControllerProps<FormValues>, 'name'>
}

export default function FormAutocomplete<
  FormValues extends FieldValues,
  OptionType extends BaseOptionType,
>({
  name,
  label,
  autoFocus = false,
  helperText,
  maxSelected,
  options,
  required,
  onChange,
  textFieldProps,
  useControllerProps,
  ...props
}: FormAutocompleteProps<FormValues, OptionType>) {
  const { field, formState } = useController<FormValues>({
    name,
    ...useControllerProps,
    rules: { required, ...useControllerProps?.rules },
  })
  const value = field.value
  return (
    <Autocomplete
      {...props}
      {...field}
      options={options}
      renderInput={(params) => (
        <TextField
          {...params}
          required={required}
          label={label}
          autoFocus={autoFocus}
          helperText={(formState.errors[name]?.message as string) || helperText}
          error={!!formState.errors[name]}
          {...textFieldProps}
          slotProps={{
            ...textFieldProps?.slotProps,
            inputLabel: {
              ...params.InputLabelProps,
              ...textFieldProps?.slotProps?.inputLabel,
            },
            input: {
              ...params.InputProps,
              ...textFieldProps?.slotProps?.input,
            },
            htmlInput: {
              ...params.inputProps,
              ...textFieldProps?.slotProps?.htmlInput,
              required: required
                ? Array.isArray(value)
                  ? value.length === 0
                  : required
                : false, // workaround to allow submitting form https://github.com/mui/material-ui/issues/21663#issuecomment-732298594
            },
          }}
        />
      )}
      onChange={(_e, newValue) => {
        field.onChange(newValue)
        onChange?.(newValue)
      }}
      getOptionDisabled={(option) => {
        if (maxSelected && Array.isArray(value)) {
          return (
            value.length >= maxSelected &&
            !value.find((v: OptionType) => v.id === option.id)
          )
        }
        return false
      }}
    />
  )
}
